import { AppConstants, CareerHubModuleReactContext } from '@careerhub/common';
import {
  Button,
  Divider,
  Subtitle1,
  Tab,
  TabList,
  Image,
  Link,
  mergeClasses,
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  Avatar,
} from '@fluentui/react-components';
import { Dismiss24Regular, DividerTall24Regular, Emoji24Regular, Question24Regular } from '@fluentui/react-icons';
import React, { FC, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { RoutingConstants } from '../../Constant';
import { FeatureFlights } from '../../utilities/FeatureFlights';
import { FeatureName, SubFeatureName } from '../resources/TelemetryEventDetails';
import { headerStyles } from './Header.styles';
import { FeedbackOptions } from '../../utilities/ocv/FeedbackOptions';
import { enforceFeedbackEmailRequired } from '../../utilities/ocv/feedbackA11y';
import { enforceFeedbackErrorFocus, stopEnforcingFeedbackErrorFocus } from '../../utilities/ocv/feedbackErrorFocus';
import {
  enforceFeedbackFocusRestoration,
  restoreFeedbackTriggerFocus,
  stopEnforcingFeedbackFocusRestoration,
} from '../../utilities/ocv/feedbackFocusRestoration';
import {
  enforceFeedbackFocusVisible,
  stopEnforcingFeedbackFocusVisible,
} from '../../utilities/ocv/feedbackFocusVisible';
import {
  enforceFeedbackScreenshotAnnouncement,
  stopEnforcingFeedbackScreenshotAnnouncement,
} from '../../utilities/ocv/feedbackScreenshotA11y';
import { FeedbackInitData, launchFeedback } from '@ms/inapp-feedback-loader';
import { Dropdown } from '../Shared/Dropdown';
import { linkGreen } from '../Shared/Shared';
import { faqContent } from '../resources/resources';
import { ProfileCenter } from '@coherence-design-system/profile-center';
import { User } from '@microsoft/microsoft-graph-types';
import { IPublicClientApplication } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { TooltipHost, TooltipOverflowMode } from '@fluentui/react';

let popUpWindow: any = null;
const openAva = (botURL: string) => {
  var w = 600;
  var h = 630;

  var dualScreenLeft = window.screenLeft != undefined ? window.screenLeft : window.screenX;
  var dualScreenTop = window.screenTop != undefined ? window.screenTop : window.screenY;
  var _width = window.innerWidth
    ? window.innerWidth
    : document.documentElement.clientWidth
    ? document.documentElement.clientWidth
    : screen.width;
  var _height = window.innerHeight
    ? window.innerHeight
    : document.documentElement.clientHeight
    ? document.documentElement.clientHeight
    : screen.height;
  var _left = dualScreenLeft + _width - w - 35;
  var _top = dualScreenTop + _height - h - 20;
  _width = w;
  _height = h > _height ? _height : h;

  var windowSpec =
    'width=' +
    _width +
    ', height=' +
    _height +
    ', top=' +
    _top +
    ', left=' +
    _left +
    ', popup=true, directories=no, titlebar=no, toolbar=no, status=no, menubar=no, scrollbars=no, resizable=no, fullscreen=no';

  if (popUpWindow == null || popUpWindow.closed) {
    popUpWindow = window.open(botURL, 'AskHRVirtualAssitant', windowSpec);
  } else {
    popUpWindow.focus();
  }
};

/** Very top of the app: app name and a tab for each page */
export const Header: FC = () => {
  const history = useHistory();
  const location = useLocation();
  const styles = headerStyles();
  const [openFAQ, setOpenFAQ] = useState<boolean>(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState<string>('');
  const { telemetryClient, featureService, graphService } = React.useContext(CareerHubModuleReactContext);
  const isCdpTabVisible = featureService.isFeatureEnabled(FeatureFlights.CDPTabVisibility);
  const faqRef = React.useRef<HTMLAnchorElement | HTMLButtonElement>(null);
  const faqCloseRef = React.useRef<HTMLButtonElement>(null);
  const feedbackRef = React.useRef<HTMLAnchorElement | HTMLButtonElement>(null);

  const [userAvatar, setUserAvatar] = useState<string>();
  const [userData, setUserData] = React.useState<User>();
  const [isPanelOpen, setIsPanelOpen] = React.useState(false);
  /* Sets the menu that will be active */
  useEffect(() => {
    if (location.pathname === RoutingConstants.CareerPlan) setSelectedMenuItem(RoutingConstants.CareerPlan);
    else if (location.pathname === RoutingConstants.CompareRoute) setSelectedMenuItem(RoutingConstants.CompareRoute);
    else if (location.pathname === RoutingConstants.AboutRoute) setSelectedMenuItem(RoutingConstants.AboutRoute);
    else setSelectedMenuItem(RoutingConstants.HomeRoute);
  }, [location?.pathname]);
  const prevOpenFAQ = React.useRef(openFAQ);
  React.useEffect(() => {
    if (prevOpenFAQ.current && !openFAQ) {
      faqRef.current?.focus();
    }
    prevOpenFAQ.current = openFAQ;
  }, [openFAQ]);

  React.useEffect(() => {
    if (!openFAQ) return;

    const animationFrame = window.requestAnimationFrame(() => faqCloseRef.current?.focus());
    return () => window.cancelAnimationFrame(animationFrame);
  }, [openFAQ]);

  const fetchData = async () => {
    try {
      const userDetails = await graphService.getMyDetails();
      if (userDetails) setUserData(userDetails);
    } catch (e) {
      console.log('graph failed' + e);
    }
    const photo = await graphService.getMyPhoto();
    setUserAvatar(photo || '');
  };
  const { instance } = useMsal();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function signOutClickHandler(instance: IPublicClientApplication) {
    const logoutRequest = {
      account: instance.getActiveAccount(),
      postLogoutRedirectUri: 'https://www.microsoft.com/',
    };
    instance.logoutRedirect(logoutRequest);
  }

  const profileCenterProps = {
    isOpen: isPanelOpen,
    onDismiss: () => setIsPanelOpen(false),
    popupProps: {
      ariaLabel: 'Profile',
    },
    focusTrapZoneProps: {
      firstFocusableTarget: '.rl-profile-first-action',
    },
    styles: {
      subComponentStyles: {
        panel: {
          main: {
            selectors: {
              '@media screen and (-ms-high-contrast: active), (forced-colors: active)': {
                border: '2px solid CanvasText',
                boxSizing: 'border-box',
              },
            },
          },
        },
        persona: {
          details: {
            minWidth: 0,
            maxWidth: '100%',
          },
          primaryText: {
            height: 'auto',
            lineHeight: 'normal',
            overflow: 'visible',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            selectors: {
              '.ms-TooltipHost': {
                display: 'block',
                width: '100%',
                overflow: 'visible',
                whiteSpace: 'normal',
                textOverflow: 'clip',
                overflowWrap: 'anywhere',
              },
            },
          },
          secondaryText: {
            height: 'auto',
            lineHeight: 'normal',
            overflow: 'visible',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          },
        },
      },
    },
    persona: {
      imageUrl: userAvatar,
      text: userData?.displayName || '',
      secondaryText: userData?.mail || '',
      onRenderPrimaryText: () => (
        <TooltipHost overflowMode={TooltipOverflowMode.Self} content={userData?.displayName || ''}>
          {userData?.displayName || ''}
        </TooltipHost>
      ),
    },
  };

  return (
    <>
      <div className={mergeClasses(styles.stickyHeader, styles.headerBackGroundColor)}>
        <div className={styles.headerContainer} role="banner">
          <div className={mergeClasses(styles.leftAlignedItem, styles.leftAlignedItemFlightStyle)}>
            <>
              <a
                href="https://microsoft.sharepoint.com/sites/hrweb"
                className={styles.brandStyle}
                aria-label="HR Web"
                target="_blank"
                title="HR Web"
              >
                HR Web
              </a>
              <DividerTall24Regular />
            </>
            <Subtitle1
              className={styles.subtitle}
              as="span"
              tabIndex={0}
              role="link"
              onClick={() => {
                history.push('/');
              }}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  history.push('/');
                }
              }}
              aria-label="Role Library"
            >
              {__APP_NAME__}
            </Subtitle1>
            {isCdpTabVisible && (
              <TabList
                selectedValue={selectedMenuItem}
                reserveSelectedTabSpace={false}
                onTabSelect={(_event, data) => {
                  if (typeof data.value !== 'string') return;

                  setSelectedMenuItem(data.value);
                  history.push(data.value);
                  telemetryClient.logButtonClick(FeatureName.HomePage, SubFeatureName.TabList, {
                    tabName: data.value,
                  });
                }}
              >
                <Tab value={RoutingConstants.HomeRoute}>Role Library</Tab>
              </TabList>
            )}
          </div>
          <div className={styles.rightAlignedItem}>
            <>
              <Button
                appearance="transparent"
                icon={<Question24Regular style={{ color: 'white' }} />}
                onClick={() => {
                  setOpenFAQ(true);
                  telemetryClient.logButtonClick(FeatureName.FAQ, SubFeatureName.OpenFaq, {});
                }}
                aria-label="Help"
                ref={faqRef}
              />
              <OverlayDrawer
                open={openFAQ}
                onOpenChange={(_event, data) => {
                  setOpenFAQ(data.open);
                  if (!data.open) {
                    requestAnimationFrame(() => faqRef.current?.focus());
                  }
                }}
                position="end"
                style={{
                  width: 600,
                }}
              >
                <DrawerHeader>
                  <DrawerHeaderTitle
                    action={
                      <Button
                        ref={faqCloseRef}
                        appearance="subtle"
                        aria-label="Close"
                        icon={<Dismiss24Regular />}
                        onClick={() => {
                          setOpenFAQ(false);
                          requestAnimationFrame(() => faqRef.current?.focus());
                        }}
                      />
                    }
                  >
                    FAQ
                  </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody style={{ paddingRight: '39px' }}>
                  {faqContent.map((content, index) => (
                    <Dropdown
                      key={index}
                      title={content.header}
                      mainContent={content.mainContent}
                      listItems={content?.listItems}
                      dropdownIndex={index}
                    />
                  ))}
                  <div>
                    {' '}
                    <div className={styles.link}>
                      <Link
                        className={styles.faqLink}
                        style={{ color: linkGreen, fontWeight: 600 }}
                        href={AppConstants.FAQLink}
                        onClick={() => {
                          telemetryClient.logButtonClick(FeatureName.FAQ, SubFeatureName.MoreFaq, {});
                        }}
                        target={'_blank'}
                      >
                        More FAQ's
                      </Link>
                    </div>
                    <a
                      href="https://aka.ms/askhronteams"
                      className={styles.linkButton}
                      aria-label="Ask HR"
                      title="Get help from Ask HR"
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => {
                        e.preventDefault();
                        telemetryClient.logButtonClick(FeatureName.AskHr, 'Opened');
                        openAva('https://aka.ms/askhronteams');
                      }}
                    >
                      <Image src="/assets/askHR.svg" alt="" />
                    </a>
                  </div>
                </DrawerBody>
              </OverlayDrawer>
            </>
            <Button
              appearance="transparent"
              icon={<Emoji24Regular style={{ color: 'white' }} />}
              onClick={async () => {
                try {
                  let feedbackInfo: FeedbackInitData;
                  let inAppFeedbackRef = document.getElementById('inAppFeedbackRef-id');
                  const feedbackOptions = {
                    ...FeedbackOptions,
                    userId: 'a:' + instance.getActiveAccount()?.homeAccountId,
                    callbackFunctions: {
                      getUserAccessToken: async () => {
                        const authResult = await instance.acquireTokenSilent({
                          scopes: [
                            'openid',
                            'profile',
                            'User.Read',
                            'https://petrol.office.microsoft.com/Feedback.ReadWrite',
                          ],
                          account: instance.getActiveAccount() || instance.getAllAccounts()[0],
                        });
                        return {
                          token: authResult.accessToken,
                        };
                      },
                      onDismiss: () => {
                        // Stop the error-focus observer once the feedback UI closes
                        // so it does not keep reacting to document mutations.
                        stopEnforcingFeedbackErrorFocus();
                        stopEnforcingFeedbackFocusRestoration();
                        stopEnforcingFeedbackFocusVisible();
                        stopEnforcingFeedbackScreenshotAnnouncement();
                        restoreFeedbackTriggerFocus(feedbackRef.current);
                      },
                    },
                  };
                  if (inAppFeedbackRef) {
                    feedbackInfo = {
                      hostName: 'RoleLibrary',
                      locale: 'en',
                      inAppFeedbackRef: inAppFeedbackRef,
                    };
                    await launchFeedback(feedbackInfo, feedbackOptions);
                    enforceFeedbackEmailRequired(inAppFeedbackRef);
                    enforceFeedbackErrorFocus(inAppFeedbackRef);
                    enforceFeedbackFocusRestoration(inAppFeedbackRef);
                    enforceFeedbackFocusVisible(inAppFeedbackRef);
                    enforceFeedbackScreenshotAnnouncement(inAppFeedbackRef);
                    console.log('Feedback SDK initialized');
                  } else console.log('inAppFeedback initialization failed');
                } catch (e) {
                  console.log(`feedback error initialization error ${e}`);
                }
                telemetryClient.logButtonClick(FeatureName.OCVFeedback, SubFeatureName.OpenFeedback, {});
              }}
              aria-label="Page Feedback"
              ref={feedbackRef}
            />
            <Button
              icon={<Avatar aria-hidden="true" image={{ src: userAvatar }} />}
              appearance="transparent"
              style={{ paddingLeft: '16px', paddingRight: '16px', marginLeft: '8px' }}
              onClick={() => setIsPanelOpen(!isPanelOpen)}
              aria-label="Profile"
            />
          </div>
        </div>

        <Divider />
      </div>
      <ProfileCenter {...profileCenterProps} style={{ top: '54px' }}>
        <div slot="footer">
          <Button
            type="button"
            appearance="transparent"
            className={`${styles.profileActionButton} rl-profile-first-action`}
            onClick={() => signOutClickHandler(instance)}
          >
            Sign out
          </Button>
        </div>
      </ProfileCenter>
    </>
  );
};
